import React, { useEffect } from "react";
import { useRouteError, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";

// react-router `errorElement` for the section routes. Without it, ANY uncaught
// render error in a tab (a component throwing, or a lazy chunk failing to fetch
// after a redeploy replaced the hashed filenames) would bubble to the data
// router's default error page and blank the whole screen. Mounted on a PATHLESS
// layout route that sits UNDER <RootLayout/> (Header) but ABOVE the sections, so
// the failure is contained to the Outlet area — the header/nav stay alive.
// (Supersedes the old <ViewErrorBoundary> class boundary; the data router
// intercepts route-render errors before an ancestor React boundary would.)

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  // Vite emits "Failed to fetch dynamically imported module"; webpack-style
  // builds emit "ChunkLoadError" / "Loading chunk N failed". Either way the
  // deployed bundle moved out from under this session — a reload fixes it.
  return /dynamically imported module|ChunkLoadError|Loading chunk/i.test(msg);
}

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const stale = isChunkLoadError(error);
  const message = error instanceof Error ? error.message : String(error ?? "Unknown error");

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="rounded border border-status-bad/40 bg-status-bad-soft p-5 text-sm text-status-bad">
        <p className="mb-2 font-semibold">This tab failed to load.</p>
        {stale ? (
          <p className="mb-3">
            The site was updated since this page was opened, so this tab&apos;s code couldn&apos;t
            be fetched. Reload the page to pick up the new version — the other tabs still work
            in the meantime.
          </p>
        ) : (
          <p className="mb-3">
            Something went wrong rendering this tab. The rest of the app is unaffected — switch
            to another tab, or retry below. The error has been reported automatically.
          </p>
        )}
        <p className="mb-4 break-all font-mono text-xs text-status-bad/90">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="rounded bg-qw-teal px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:opacity-90"
          >
            Reload page
          </button>
          {!stale && (
            <Link
              to="/"
              className="rounded border border-status-bad/50 px-4 py-2 text-xs font-medium text-status-bad hover:bg-status-bad-soft"
            >
              Back to home
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteErrorBoundary;
